import React, { useState, useEffect, useMemo, ReactNode } from 'react';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import ScrollReveal from '@components/common/ScrollReveal';
import argJRLogo from '@images/home/clubs/aa_argentinos_junior.webp';
import caiLogo from '@images/home/clubs/ca_independiente.webp';
import olimpiaLogo from '@images/home/clubs/olimpia.webp';
import emelecLogo from '@images/home/clubs/emelec.webp';
import alianzaLimaLogo from '@images/home/clubs/alianza_lima.webp';
import * as styles from './clubsNetwork.module.scss';

interface ClubData {
  name: string;
  logo: ReactNode;
}

const clubs: ClubData[] = [
  {
    name: 'EMELEC',
    logo: (
      <img
        src={emelecLogo}
        alt={'Club Sport Emelec'}
        title={'Club Sport Emelec'}
        loading={'lazy'}
        decoding={'async'}
        className={styles.logo}
        width={80}
        height={80}
      />
    ),
  },
  {
    name: 'AAAJ',
    logo: (
      <img
        src={argJRLogo}
        alt={'Argentinos Juniors'}
        title={'Argentinos Juniors'}
        loading={'lazy'}
        decoding={'async'}
        width={80}
        height={80}
        className={styles.logo}
      />
    ),
  },
  {
    name: 'CAI',
    logo: (
      <img
        src={caiLogo}
        alt={'Club Atlético Independiente'}
        title={'Club Atlético Independiente'}
        loading={'lazy'}
        decoding={'async'}
        width={80}
        height={80}
        className={styles.logo}
      />
    ),
  },
  {
    name: 'OLIMPIA',
    logo: (
      <img
        src={olimpiaLogo}
        alt={'Club Olimpia'}
        title={'Club Olimpia'}
        loading={'lazy'}
        decoding={'async'}
        width={80}
        height={80}
        className={styles.logo}
      />
    ),
  },
  {
    name: 'ALIANZA',
    logo: (
      <img
        src={alianzaLimaLogo}
        alt={'Club Alianza Lima'}
        title={'Club Alianza Lima'}
        loading={'lazy'}
        decoding={'async'}
        width={80}
        height={80}
        className={styles.logo}
      />
    ),
  },
];

const ITEM_HEIGHT = 80;
const GAP = 36;

const ClubsNetwork: React.FC = () => {
  const { t } = useTranslation('rwa');

  const extendedClubs = useMemo(() => [...clubs, ...clubs, ...clubs], []);
  const [activeIndex, setActiveIndex] = useState(clubs.length);
  const [isTransitioning, setIsTransitioning] = useState(true);

  const stats = useMemo(
    () => [
      { value: '231', label: t('banner_stat_history') || 'Tokenized Player History' },
      { value: '18', label: t('partner_clubs') || 'Partner Clubs' },
      { value: '8', label: t('banner_stat_countries') || 'Different Countries' },
    ],
    [t],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleTransitionEnd = () => {
    if (activeIndex >= clubs.length * 2) {
      setIsTransitioning(false);
      setActiveIndex(activeIndex - clubs.length);
    } else if (activeIndex < clubs.length) {
      setIsTransitioning(false);
      setActiveIndex(activeIndex + clubs.length);
    }
  };

  useEffect(() => {
    if (!isTransitioning) {
      const ref = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitioning(true);
        });
      });
      return () => cancelAnimationFrame(ref);
    }
  }, [isTransitioning]);

  const centerIndex = activeIndex + 2;

  return (
    <ScrollReveal
      className={styles.container}
      direction={'up'}
      distance={60}
      threshold={'90%'}
      end={'center center'}
      scrub={1}
      role={'region'}
      aria-labelledby={'clubs-network-title'}
    >
      <div className={styles.content}>
        <div className={styles.infoSide}>
          <h2 id={'clubs-network-title'} className={styles.title}>
            <Trans components={{ br: <br />, b: <span className={styles.greenText} /> }}>
              {t('clubs_network_title') || 'A GROWING NETWORK <br /><b>OF CLUBS</b>'}
            </Trans>
          </h2>
          <p className={styles.subtitle}>
            {t('clubs_network_subtitle') ||
              'An expanding group of clubs using Club Transfer Tokens to unlock the value of player development.'}
          </p>
          <div
            className={styles.statsRow}
            role={'list'}
            aria-label={'Network Statistics'}
          >
            {stats.map((stat) => (
              <div key={stat.label} className={styles.statItem} role={'listitem'}>
                <span className={styles.statValue}>{stat.value}</span>
                <span className={styles.statLabel}>{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.sliderSide} aria-hidden={'true'}>
          <div className={styles.sliderTrack}>
            <div
              className={styles.sliderContent}
              onTransitionEnd={handleTransitionEnd}
              style={{
                transition: isTransitioning
                  ? 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
                  : 'none',
                transform: `translateY(-${activeIndex * (ITEM_HEIGHT + GAP)}px)`,
              }}
            >
              {extendedClubs.map((club, index) => {
                const isActive = index === centerIndex;
                const isNear = Math.abs(index - centerIndex) <= 2 && !isActive;

                return (
                  <div
                    key={`${club.name}-${index}`}
                    className={`${styles.clubItem} ${isActive ? styles.active : ''} ${isNear ? styles.near : ''}`}
                  >
                    <div className={styles.logoWrapper}>{club.logo}</div>
                    <span className={styles.clubName}>{club.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default React.memo(ClubsNetwork);
